import { Link } from 'react-router-dom'
import { Phone, Mail, MapPin, Clock, ExternalLink } from 'lucide-react'
import { SITE, NAV_LINKS } from '../content/siteContent'

export default function Footer() {
  const year = new Date().getFullYear()

  return (
    <footer className="bg-brand-navy text-white" role="contentinfo">
      {/* Main footer */}
      <div className="section-container py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* Brand column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4 focus:outline-none focus:ring-2 focus:ring-brand-sky rounded-lg p-1 -ml-1" aria-label="Grace Pointe home">
              <div className="w-10 h-10 bg-brand-blue rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-white font-display font-bold text-lg">GP</span>
              </div>
              <div>
                <p className="font-display font-bold text-white leading-tight">Grace Pointe</p>
                <p className="text-brand-sky text-xs font-round font-semibold tracking-wide">Child Development Center</p>
              </div>
            </Link>
            <p className="text-slate-300 text-sm leading-relaxed mt-3">
              A Christ-centered, multicultural early learning community serving families in Avon and the western Indianapolis area.
            </p>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-white font-round font-bold text-sm tracking-wider uppercase mb-4">Quick Links</h3>
            <ul className="space-y-2" role="list">
              {NAV_LINKS.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-slate-300 hover:text-white text-sm transition-colors focus:outline-none focus:underline"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href={SITE.procare.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-white text-sm transition-colors focus:outline-none focus:underline flex items-center gap-1"
                >
                  Parent Portal <ExternalLink size={12} aria-hidden="true" />
                </a>
              </li>
            </ul>
          </div>

          {/* Contact info */}
          <div>
            <h3 className="text-white font-round font-bold text-sm tracking-wider uppercase mb-4">Contact Us</h3>
            <ul className="space-y-3" role="list">
              <li>
                <a
                  href={`tel:${SITE.contact.phone.replace(/\D/g, '')}`}
                  className="flex items-start gap-3 text-slate-300 hover:text-white text-sm transition-colors group focus:outline-none focus:underline"
                >
                  <Phone size={15} className="mt-0.5 flex-shrink-0 text-brand-sky group-hover:text-brand-amber transition-colors" aria-hidden="true" />
                  {SITE.contact.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${SITE.contact.email}`}
                  className="flex items-start gap-3 text-slate-300 hover:text-white text-sm transition-colors group focus:outline-none focus:underline break-all"
                >
                  <Mail size={15} className="mt-0.5 flex-shrink-0 text-brand-sky group-hover:text-brand-amber transition-colors" aria-hidden="true" />
                  {SITE.contact.email}
                </a>
              </li>
              <li>
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(SITE.contact.address.display)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-slate-300 hover:text-white text-sm transition-colors group focus:outline-none focus:underline"
                >
                  <MapPin size={15} className="mt-0.5 flex-shrink-0 text-brand-sky group-hover:text-brand-amber transition-colors" aria-hidden="true" />
                  <span>{SITE.contact.address.display}</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h3 className="text-white font-round font-bold text-sm tracking-wider uppercase mb-4">Hours of Operation</h3>
            <div className="flex items-start gap-3 text-slate-300 text-sm">
              <Clock size={15} className="mt-0.5 flex-shrink-0 text-brand-sky" aria-hidden="true" />
              <div>
                <p className="font-semibold text-white">Monday – Friday</p>
                <p>7:00 AM – 5:30 PM</p>
                {/* TODO: Confirm holiday closures schedule with owner */}
                <p className="mt-1 text-xs text-slate-400">Closed on major holidays</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="section-container py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-400">
          <p>© {year} {SITE.name}. All rights reserved.</p>
          <p>Serving Avon, Plainfield &amp; the western Indianapolis community</p>
        </div>
      </div>
    </footer>
  )
}
